import type {NumberLike} from "#template/nodes/types"
import type {BinaryMathOperatorOption, MathOperatorOption, UnaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import {isBinaryMathOperatorOption, isUnaryMathOperatorOption} from "#template/nodes/geometry-graph/options"
import type {
    PointGeometryAttributeLikeNode,
    CurveGeometryAttributeLikeNode,
    MeshGeometryAttributeLikeNode,
} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {PointGeometryUnaryMathOperator} from "#template/nodes/geometry-graph/attribute/unary-math-operator/point-geometry-unary-math-operator"
import {CurveGeometryUnaryMathOperator} from "#template/nodes/geometry-graph/attribute/unary-math-operator/curve-geometry-unary-math-operator"
import {MeshGeometryUnaryMathOperator} from "#template/nodes/geometry-graph/attribute/unary-math-operator/mesh-geometry-unary-math-operator"
import {PointGeometryBinaryMathOperator} from "#template/nodes/geometry-graph/attribute/binary-math-operator/point-geometry-binary-math-operator"
import {CurveGeometryBinaryMathOperator} from "#template/nodes/geometry-graph/attribute/binary-math-operator/curve-geometry-binary-math-operator"
import {MeshGeometryBinaryMathOperator} from "#template/nodes/geometry-graph/attribute/binary-math-operator/mesh-geometry-binary-math-operator"
import type {GeometryValueNode} from "#template/nodes/geometry-graph/value/value-node"

export function createPointGeometryUnaryOrBinaryMathOperator(
    operator: MathOperatorOption,
    arg1: PointGeometryAttributeLikeNode | null,
    arg2?: NumberLike | PointGeometryAttributeLikeNode | GeometryValueNode | null,
): PointGeometryUnaryMathOperator | PointGeometryBinaryMathOperator {
    if (isUnaryMathOperatorOption(operator)) {
        return new PointGeometryUnaryMathOperator({parameters: {arg: arg1, operator: operator as UnaryMathOperatorOption}})
    } else if (isBinaryMathOperatorOption(operator)) {
        if (arg2 === undefined) throw new Error("Binary math operator requires two arguments")
        return new PointGeometryBinaryMathOperator({parameters: {arg1, arg2, operator: operator as BinaryMathOperatorOption}})
    } else {
        throw new Error(`Unknown math operator: ${operator}`)
    }
}

export function createCurveGeometryUnaryOrBinaryMathOperator(
    operator: MathOperatorOption,
    arg1: CurveGeometryAttributeLikeNode | null,
    arg2?: NumberLike | CurveGeometryAttributeLikeNode | GeometryValueNode | null,
): CurveGeometryUnaryMathOperator | CurveGeometryBinaryMathOperator {
    if (isUnaryMathOperatorOption(operator)) {
        return new CurveGeometryUnaryMathOperator({parameters: {arg: arg1, operator: operator as UnaryMathOperatorOption}})
    } else if (isBinaryMathOperatorOption(operator)) {
        if (arg2 === undefined) throw new Error("Binary math operator requires two arguments")
        return new CurveGeometryBinaryMathOperator({parameters: {arg1, arg2, operator: operator as BinaryMathOperatorOption}})
    } else {
        throw new Error(`Unknown math operator: ${operator}`)
    }
}

export function createMeshGeometryUnaryOrBinaryMathOperator(
    operator: MathOperatorOption,
    arg1: MeshGeometryAttributeLikeNode | null,
    arg2?: NumberLike | MeshGeometryAttributeLikeNode | GeometryValueNode | null,
): MeshGeometryUnaryMathOperator | MeshGeometryBinaryMathOperator {
    if (isUnaryMathOperatorOption(operator)) {
        return new MeshGeometryUnaryMathOperator({parameters: {arg: arg1, operator: operator as UnaryMathOperatorOption}})
    } else if (isBinaryMathOperatorOption(operator)) {
        if (arg2 === undefined) throw new Error("Binary math operator requires two arguments")
        return new MeshGeometryBinaryMathOperator({parameters: {arg1, arg2, operator: operator as BinaryMathOperatorOption}})
    } else {
        throw new Error(`Unknown math operator: ${operator}`)
    }
}
